import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface ModalLayoutProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
  maxHeight?: string;
  zIndexClass?: string;
  usePortal?: boolean;
  headerRight?: React.ReactNode;
}

export default function ModalLayout({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = "max-w-4xl",
  maxHeight = "max-h-[90vh]",
  zIndexClass = "z-50",
  usePortal = false,
  headerRight,
}: ModalLayoutProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // 다음 프레임에서 애니메이션 시작
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // 애니메이션 완료 후 DOM에서 제거
      setTimeout(() => setShouldRender(false), 200);
    }
  }, [isOpen]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // 모달이 열려 있는 동안 body 스크롤 방지 (닫힐 때 원래 상태 복원)
  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const modalNode = (
    <div className={`fixed inset-0 ${zIndexClass} flex items-center justify-center`}>
      {/* 배경 오버레이 - 페이드 인/아웃 */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ${isAnimating ? "opacity-50" : "opacity-0"}`}
        onClick={onClose}
      />
      {/* 모달 컨테이너 - 페이드 + 스케일 애니메이션 */}
      <div
        className={`relative bg-white rounded-lg shadow-xl w-full ${maxWidth} mx-4 ${maxHeight} overflow-y-auto transform transition-all duration-200 ${
          isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-xl font-semibold text-[#333333]">{title}</h2>
          <div className="flex items-center gap-4">
            {headerRight}
            <button onClick={onClose} className="text-[#333333] hover:text-[#888888] transition-colors cursor-pointer">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 26 26">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {/* 모달 본문 */}
        <div className="px-6 pb-6">{children}</div>
      </div>
    </div>
  );

  return usePortal ? createPortal(modalNode, document.body) : modalNode;
}
